import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../AuthGate';

// SWC — shift checklist. Each task is a checkbox; checking it immediately records
// who checked it (from the authenticated user) and the exact time, saved to the
// shared cache with NO save button. Unchecking clears the entry. Scoped by
// date/shift so the historical record is browsable via the selectors.

// Standard Work Checklist. Items are grouped under numbered section headers
// (the `section` field). Each item is a checkbox that records who/when on check.
// `id` is the persisted key — keep stable to preserve historical check records.
const TASKS = [
  // 1. Pallet Receive (PR) / Pallet Build
  { section: '1. Pallet Receive (PR) / Pallet Build', id: 'pr_lineStaffing', label: 'PR pallet build staffing: Is each line staffed to match the flow of the line?' },
  { section: '1. Pallet Receive (PR) / Pallet Build', id: 'pr_lantech', label: 'Lantech staffing: Confirm Lantech is staffed to match WIP/flow. More than two should be a flag. Are Lantech operators operating both Lantechs as per standard work?' },
  { section: '1. Pallet Receive (PR) / Pallet Build', id: 'pr_problemSolve', label: 'Problem solve staffing reviewed: Piles (planned/entitlement) compared to actual on-path. PID-volume contingent; scale down when PID volume is low.' },

  // 2. PID / Mainsorter
  { section: '2. PID / Mainsorter', id: 'pid_operator', label: 'PID operator not staffed. PA should cover this role, TDR or reinjector/downstacker can assist in high-flow situation. Until volume truly ramps up on sorter, this is an extraneous role.' },
  { section: '2. PID / Mainsorter', id: 'pid_merger', label: 'Merger: Merger should only be staffed if two doors are being operating on the same PID. A single line feeding a PID does not require a merger.' },
  { section: '2. PID / Mainsorter', id: 'pid_lineCount', label: 'PID line count matched to actual carton flow for the quarter, not a fixed line assumption.' },

  // 3. EOL / AROS
  { section: '3. EOL / AROS', id: 'eol_avery', label: 'Avery scanning at EOL reviewed. Two scanners only when multiple lines are run. This should be the exception and not the norm.' },
  { section: '3. EOL / AROS', id: 'eol_divert', label: 'Divert staffing reviewed. This should be limited on EOL only to volume surges.' },
  { section: '3. EOL / AROS', id: 'eol_lines', label: 'EOL Lines: Multiple lines should only be run if there\u2019s increased volume from door closures. Exception, not the norm. Review IDRT rates for prior quarter to confirm flow/staffing match. Low rates indicate overstaffing to volume.' },
  { section: '3. EOL / AROS', id: 'aros_lineStaffing', label: 'AROS Line Staffing and ARC Allocation: Two sorters staffed per line; review previous quarter IDRT rates to confirm volume flow is appropriately allocated by ARC. Look for upstream bottlenecks.' },
  { section: '3. EOL / AROS', id: 'eolaros_dynamicStaffing', label: 'Dynamic Staffing: Are leaders moving sorters to the volume?' },

  // 4. Jackpot
  { section: '4. Jackpot', id: 'jackpot_aaCoverage', label: 'Jackpot AA also covers Jackpot reinject; confirm single-AA coverage holds for the volume.' },

  // 5. Cross-path confirmation
  { section: '5. Cross-path confirmation', id: 'xpath_waterspider', label: 'All waterspider buckets across every path reviewed. Waterspider is one of the more waste-prone roles across all paths. Confirm Fluid/PR/EOL/AROS Waterspider paths are staffed to flow and not being used as a \u201Ccatch-all\u201D for late arrivals.' },
  { section: '5. Cross-path confirmation', id: 'xpath_flowVolume', label: 'Every path confirmed staffed to flow and volume, with AAs empowered to move to the volume.' },
  { section: '5. Cross-path confirmation', id: 'xpath_oppLogged', label: 'Any opportunity identified this quarter logged with owner + ECD and tracked at daily cost sync.' },
];

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

// Normalize a loaded record's checks into the quarter-keyed shape
// { Q1: {taskId:{...}}, ... }. Legacy flat records { taskId:{...} } are
// migrated into Q1 so historical checks are preserved.
function normalizeChecks(raw) {
  const out = { Q1: {}, Q2: {}, Q3: {}, Q4: {} };
  if (!raw || typeof raw !== 'object') return out;
  const keys = Object.keys(raw);
  const isQuarterKeyed = keys.some(k => QUARTERS.includes(k));
  if (isQuarterKeyed) {
    QUARTERS.forEach(q => { if (raw[q] && typeof raw[q] === 'object') out[q] = raw[q]; });
  } else {
    out.Q1 = raw; // legacy flat checks -> Q1
  }
  return out;
}

export default function SWC({ api, site }) {
  const { user } = useContext(AuthContext) || {};
  const who = (user && user.username) || 'unknown';

  const [date, setDate] = useState('');
  const [shiftType, setShiftType] = useState('day');
  const [quarter, setQuarter] = useState('Q1');
  const [checks, setChecks] = useState({}); // { quarter: { taskId: { checked, by, at } } }
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState('');

  useEffect(() => {
    const d = new Date();
    const pst = new Date(d.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    if (pst.getHours() < 6) pst.setDate(pst.getDate() - 1);
    setDate(`${pst.getFullYear()}-${String(pst.getMonth()+1).padStart(2,'0')}-${String(pst.getDate()).padStart(2,'0')}`);
  }, []);

  const cacheKey = `SWC_${date}`;

  const load = useCallback(() => {
    if (!date) return;
    fetch(`${api}/api/fclm/cache?site=${site}&date=${cacheKey}&shift_type=${shiftType}`)
      .then(r => r.json())
      .then(d => setChecks(normalizeChecks(d && d.checks)))
      .catch(() => setChecks({ Q1: {}, Q2: {}, Q3: {}, Q4: {} }));
  }, [api, site, date, shiftType, cacheKey]);

  useEffect(() => { setLoading(true); load(); setLoading(false); }, [load]);

  // Poll every 30s so multiple viewers see live updates.
  useEffect(() => {
    if (!date) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load, date]);

  const persist = async (next) => {
    await fetch(`${api}/api/fclm/cache`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site, date: cacheKey, shift_type: shiftType, data: { checks: next } }),
    });
  };

  const toggle = async (taskId, isChecked) => {
    setSavingId(taskId);
    const next = { Q1: {}, Q2: {}, Q3: {}, Q4: {}, ...checks };
    const q = { ...(next[quarter] || {}) };
    if (isChecked) {
      q[taskId] = { checked: true, by: who, at: new Date().toISOString() };
    } else {
      delete q[taskId]; // uncheck just clears
    }
    next[quarter] = q;
    setChecks(next); // optimistic
    try { await persist(next); } catch (e) { /* reload on failure to resync */ load(); }
    setSavingId('');
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }); }
    catch { return iso; }
  };

  return (
    <div>
      <h2 className="page-title">SWC</h2>
      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <label style={{ fontSize: '0.75rem' }}>Date: <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ fontSize: '0.75rem' }} /></label>
        <div>{['day','night'].map(s => <button key={s} className={`btn ${shiftType===s?'btn-primary':'btn-secondary'}`} onClick={() => setShiftType(s)} style={{ fontSize: '0.7rem', padding: '4px 10px' }}>{s === 'day' ? 'Day' : 'Night'}</button>)}</div>
        <label style={{ fontSize: '0.75rem' }}>Quarter: <select value={quarter} onChange={e => setQuarter(e.target.value)} style={{ fontSize: '0.75rem' }}>{QUARTERS.map(q => <option key={q} value={q}>{q}</option>)}</select></label>
        <span style={{ fontSize: '0.7rem', color: '#666' }}>Signed in as <strong>{who}</strong></span>
        {(() => {
          // Completion donut: checked items across ALL 4 quarters / (items * 4),
          // for the selected date+shift. Always shown regardless of quarter selected.
          const totalSlots = TASKS.length * QUARTERS.length;
          let checkedCount = 0;
          QUARTERS.forEach(q => { const qc = checks[q] || {}; TASKS.forEach(t => { if (qc[t.id]?.checked) checkedCount++; }); });
          const pct = totalSlots > 0 ? Math.round((checkedCount / totalSlots) * 100) : 0;
          const R = 26, C = 2 * Math.PI * R, off = C * (1 - pct / 100);
          const color = pct >= 100 ? '#2e7d32' : pct >= 50 ? '#1565c0' : '#f57f17';
          return (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }} title={`${checkedCount} of ${totalSlots} checks complete across all quarters (this date/shift)`}>
              <svg width="64" height="64" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r={R} fill="none" stroke="#e0e0e0" strokeWidth="8" />
                <circle cx="32" cy="32" r={R} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={C} strokeDashoffset={off} transform="rotate(-90 32 32)" />
                <text x="32" y="36" textAnchor="middle" fontSize="14" fontWeight="700" fill="#232f3e">{pct}%</text>
              </svg>
              <span style={{ fontSize: '0.65rem', color: '#666' }}>Complete<br/>(all quarters)</span>
            </div>
          );
        })()}
      </div>

      <div className="card">
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ background: '#232f3e', color: '#fff' }}>
              <th style={{ padding: '6px 10px', width: 40 }}></th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Task</th>
              <th style={{ padding: '6px 10px', textAlign: 'left', width: 260 }}>Checked By / Time</th>
            </tr>
          </thead>
          <tbody>
            {TASKS.map((t, idx) => {
              const c = (checks[quarter] || {})[t.id];
              const done = !!(c && c.checked);
              const showHeader = idx === 0 || TASKS[idx - 1].section !== t.section;
              return (
                <React.Fragment key={t.id}>
                  {showHeader && (
                    <tr style={{ background: '#eceff1' }}>
                      <td colSpan={3} style={{ padding: '6px 10px', fontWeight: 700, color: '#1b5e20', fontSize: '0.8rem', borderTop: '2px solid #232f3e' }}>{t.section}</td>
                    </tr>
                  )}
                  <tr style={{ borderBottom: '1px solid #eee', background: done ? '#e8f5e9' : '#fff' }}>
                    <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                      <input type="checkbox" checked={done} disabled={savingId === t.id} onChange={e => toggle(t.id, e.target.checked)} style={{ width: 18, height: 18, cursor: 'pointer' }} />
                    </td>
                    <td style={{ padding: '6px 10px', fontSize: '0.85rem' }}>{t.label}</td>
                    <td style={{ padding: '6px 10px', fontSize: '0.75rem', color: '#333' }}>
                      {done ? <span>✅ <strong>{c.by}</strong> — {fmtTime(c.at)}</span> : <span style={{ color: '#aaa' }}>—</span>}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {loading && <p style={{ fontSize: '0.7rem', color: '#888' }}>Loading…</p>}
        <p style={{ fontSize: '0.62rem', color: '#888', marginTop: 6 }}>Checking a box saves instantly (who + time). Unchecking clears it. Change the date/shift above to view historical records. Updates refresh every 30s.</p>
      </div>
    </div>
  );
}
